package dev.lingvo.site.pages.app.settings.usage

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.Role
import dev.lingvo.shared.SpendGrouping
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LReceiptIcon
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableAlign
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.settings.UsageDenied
import dev.lingvo.site.pages.app.reports.spendCsv
import dev.lingvo.site.state.WorkspaceSelection
import dev.lingvo.site.util.downloadText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * The credit ledger, by provider.
 *
 * Gross and refunded stay apart here for the same reason they do everywhere else: `releaseCredits`
 * decrements the counter it incremented, so a month with 900 spent and 400 refunded is
 * indistinguishable from one with 500 spent if anything on the way collapses them.
 */
@Page("/app/settings/usage/events")
@Layout(".components.layouts.AppLayout")
@Composable
fun UsageEventsPage() {
    val ctx = rememberPageContext()
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    var refreshKey by remember { mutableStateOf(0) }

    // Hoisted so the header can state how many rows a download would carry, and still gated so a
    // translator's refused request is never sent.
    val canReadLedger = workspace == null || workspace.role.atLeast(Role.ADMIN)
    val state = loadAsync(slug, canReadLedger, refreshKey) {
        if (!canReadLedger) null else slug?.let { ApiClient.spendReport(it, SpendGrouping.PROVIDER) }
    }
    val loadedLedger = (state as? Async.Success)?.value

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.settings.usage.events.credit_ledger", "Credit ledger"),
            t("app.settings.usage.events.every_credit_reserved_and_every_one", "Every credit reserved and every one given back."),
            mark = { LReceiptIcon() },
            actions = {
                // The real count, and the scope stated under it: this writes the rows on the page,
                // over the window the page names, and not the whole history.
                loadedLedger?.rows?.takeIf { it.isNotEmpty() }?.let { rows ->
                    LButton(
                        tPlural(
                            "app.settings.usage.events.count.download_row",
                            rows.size,
                            "Download {count} row",
                            "Download {count} rows",
                        ),
                        {
                            downloadText(
                                spendCsv(loadedLedger),
                                "lingvo-credit-ledger.csv",
                                "text/csv;charset=utf-8",
                            )
                        },
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LDownloadIcon() },
                    )
                }
            },
        )

        // `GET /reports/spend` calls `requireManage()`, so a translator's load can only ever fail.
        // See `UsagePage` for why the refusal is a sentence and not a red box with a Retry.
        if (!canReadLedger) {
            UsageDenied()
            return@Column
        }

        LAsyncContent(
            state,
            rows = 5,
            loadingLabel = t("app.settings.usage.events.loading_the_ledger", "Loading the ledger"),
            // Client-owned copy. The failure's own message is whatever the server or the fetch
            // layer said, which on a money page is at best jargon and at worst a stack frame — and
            // it can imply a charge that never happened.
            failure = {
                ErrorState(
                    usageLedgerUnreadableNote(),
                    title = t("app.settings.usage.events.we_cant_read_the_ledger_right", "We can't read the ledger right now"),
                    onRetry = { refreshKey++ },
                )
            },
        ) { report ->
            when {
                // Not "choose a workspace": the picker it sent the reader to is empty, because null
                // here means this account is in no workspace at all.
                report == null -> LEmptyState(
                    t("app.settings.usage.events.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                    t("app.settings.usage.events.a_workspace_holds_the_credits_this", "A workspace holds the credits this ledger counts. Create one, or ask " +
                        "whoever invited you to send the invitation again."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.INSIGHTS,
                ) {
                    LButton(
                        t("app.settings.usage.events.create_a_workspace", "Create a workspace"),
                        { ctx.router.tryRoutingTo("/app/workspaces") },
                    )
                }
                report.rows.isEmpty() -> EmptyBlock()
                else -> {
                    // Stated in UTC, because the billing period is a UTC month and an admin in
                    // UTC+9 otherwise reconciles against the wrong days.
                    LMuted(tf(
                        "app.settings.usage.events.window_range",
                        "{from} to {to} UTC",
                        "from" to report.fromUtc.take(10),
                        "to" to report.toUtc.take(10),
                    ))
                    LTable(caption = t("app.settings.usage.events.credits_by_provider", "Credits by provider")) {
                        LTableHead {
                            LTableRow {
                                LTableHeaderCell { Text(t("app.settings.usage.events.provider", "Provider")) }
                                listOf(
                                    t("app.settings.usage.events.column_strings", "Strings"),
                                    t("app.settings.usage.events.column_reserved", "Reserved"),
                                    t("app.settings.usage.events.column_given_back", "Given back"),
                                    t("app.settings.usage.events.column_net", "Net"),
                                ).forEach { LTableHeaderCell(align = LTableAlign.END) { Text(it) } }
                            }
                        }
                        LTableBody {
                            report.rows.forEach { row ->
                                LTableRow {
                                    LTableHeaderCell(scope = LTableHeaderScope.ROW) { Text(row.provider ?: "—") }
                                    Number(row.strings)
                                    Number(row.creditsGross)
                                    Number(row.creditsRefunded)
                                    Number(row.creditsNet)
                                }
                            }
                        }
                    }
                    // "Is this all of it?" was unanswerable: the table ended and the page
                    // stopped, so a reader had no way to tell a complete range from a
                    // truncated one. This route fetches the whole range in one request, so
                    // the answer is always the same one — but it has to be on the page.
                    LMuted(t("app.settings.usage.events.thats_everything_in_this_range", "That's everything in this range."))
                    Legend()
                }
            }
        }
    }
}

/**
 * Why a project that plainly translated all month can be absent from this table.
 *
 * Rendered beside the data rather than in the page header: the question only occurs to somebody
 * once they have looked for a project and not found it.
 */
@Composable
private fun Legend() {
    LMuted(t("app.settings.usage.events.only_runs_on_lingvos_key_appear", "Only runs on Lingvo's key appear here. Your own key costs no credits and moves nothing."))
}

/**
 * Nothing spent — a plain block, not an illustrated empty state.
 *
 * This is a true and ordinary state on a money page, and the decorated `LEmptyState` treatment
 * read as "something is missing here". There is nothing to fix and therefore no button.
 */
@Composable
private fun EmptyBlock() {
    val palette = ColorMode.current.toSitePalette()
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(palette.surfaceSunken)
            .borderRadius(Radius.Card)
            .padding(Space.XL)
            .gap(Space.SM),
    ) {
        // `H2`, because `PageHeader` emits this page's only `H1` and this block replaces the table
        // directly under it — an `H3` there left a screen reader's heading list skipping a level,
        // which reads as a missing section rather than as an empty one. The type is untouched: this
        // is a heading-level correction, not a restyle of the block.
        H2(
            Modifier
                .displayFont()
                .color(palette.textPrimary)
                .fontSize(TypeScale.HeadingSmall)
                .fontWeight(700)
                .lineHeight(TypeScale.DisplayLineHeight)
                .margin(0.px)
                .toAttrs(),
        ) {
            Text(t("app.settings.usage.events.nothing_spent_this_month", "Nothing spent this month"))
        }
        P(
            Modifier
                .color(palette.textSecondary)
                .fontSize(TypeScale.BodySmall)
                .margin(0.px)
                .toAttrs(),
        ) {
            // "on the shared key", not "with" it: the run happens on somebody's key, and the
            // preposition is the one the rest of the product uses for the same fact.
            Text(t("app.settings.usage.events.credits_move_only_when_somebody_runs", "Credits move only when somebody runs a pre-translate on the shared key."))
        }
    }
}

/** A figure in a cell. Grouped, because five-digit credit counts are misread without it. */
@Composable
private fun Number(value: Int) {
    LTableCell(align = LTableAlign.END) { Text(formatCount(value)) }
}

/** What a failed read of the credit ledger says. `ErrorState`'s message is positional copy. */
internal fun usageLedgerUnreadableNote(): String = tr(
    "app.settings.usage.events.ledger_unavailable",
    "The credit ledger could not be loaded. Retry to check recorded credit usage.",
)
